import React, { useEffect, useState } from "react";
import { NavLink, Outlet, useParams, useNavigate, useLocation } from "react-router-dom";
import { fetchJobById } from "../../Redux/ActionCreators/jobsAction";
import { useDispatch, useSelector } from "react-redux";
import { AutomationContext } from "./AutomationContext";
import "./jobsV2.scss";

const JobTabsV2 = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const location = useLocation();
  const { jobId } = useParams();

  const job = useSelector((state) => state.jobs.latestJob);
  const totalCandidates = useSelector((state) => state.candidates.pagination?.total);

  const [titleLoading, setTitleLoading] = useState(false);
  const [automationControls, setAutomationControls] = useState(null);

  const pathParts = location.pathname.split("/");
  const isAutomationTab = pathParts[pathParts.length - 1] === "automation";

  useEffect(() => {
    window.dispatchEvent(new CustomEvent("CLOSE_SIDEBAR"));
  }, []);

  useEffect(() => {
    const fetchJobData = async () => {
      if (jobId) {
        setTitleLoading(true);
        await dispatch(fetchJobById(jobId));
        setTitleLoading(false);
      }
    };
    fetchJobData();
  }, [dispatch, jobId]);

  const formatPostedDate = (dateString) => {
    if (!dateString) return "";
    const date = new Date(dateString);
    return date.toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" });
  };

  const buildSubtitle = () => {
    const parts = [];
    const companyName = job?.company_name || job?.organization?.name || job?.company || "";
    if (companyName) parts.push(companyName);
    if (job?.created_at) parts.push(`Posted ${formatPostedDate(job.created_at)}`);
    if (totalCandidates != null) parts.push(`${totalCandidates} candidates`);
    return parts.join(" · ");
  };

  const subtitle = job ? buildSubtitle() : "";

  return (
    <AutomationContext.Provider value={{ controls: automationControls, setControls: setAutomationControls }}>
      <div className="job-tabs-v2">
        <div className="job-navigation-v2">
          <div className="job-nav-left">
            <button className="back-arrow-v2" onClick={() => navigate("/jobs")}>
              <img src="/images/back-arrow-black.svg" alt="Back" width={20} height={20} />
            </button>
            <div className="job-title-block">
              {titleLoading ? (
                <div className="spinner-border spinner-border-sm" role="status" style={{ color: "#F75891" }}>
                  <span className="visually-hidden">Loading...</span>
                </div>
              ) : (
                <>
                  <h3 className="job-title-v2">{job ? job.title : ""}</h3>
                  {subtitle && <p className="job-subtitle-v2">{subtitle}</p>}
                </>
              )}
            </div>
          </div>

          {isAutomationTab ? (
            <div className="auto-header-controls">
              {automationControls?.hasAutomation && (
                <div className="auto-toggle-wrap">
                  <span className="auto-toggle-label auto-toggle-label--pre">Automation</span>
                  <button
                    className={`auto-toggle-btn${automationControls.isActive ? " on" : ""}`}
                    onClick={automationControls.onToggle}
                    aria-label="Toggle automation"
                  />
                  <span className="auto-toggle-label">
                    {automationControls.isActive ? "On" : "Off"}
                  </span>
                </div>
              )}
              {automationControls?.showControls && (
                <>
                  <button className="auto-ctrl-btn" onClick={automationControls?.onFit}>Fit</button>
                  <button className="auto-ctrl-btn" onClick={automationControls?.onReset}>Reset</button>
                  <button
                    className="auto-ctrl-btn auto-ctrl-btn--primary"
                    onClick={automationControls?.onSave}
                    disabled={automationControls?.saving || !automationControls?.hasChanges}
                  >
                    {automationControls?.saving ? "Saving…" : "Save"}
                  </button>
                </>
              )}
            </div>
          ) : (
            <button
              className="add-candidate-btn-v2"
              onClick={() => navigate(`/jobs/${jobId}/invite`)}
            >
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2" />
                <circle cx="9" cy="7" r="4" />
                <line x1="19" y1="8" x2="19" y2="14" />
                <line x1="22" y1="11" x2="16" y2="11" />
              </svg>
              Invite Candidates
            </button>
          )}
        </div>

        <div className="tabs-header-v2">
          <nav className="tabs-nav-v2">
            <NavLink
              to={`/jobs/${jobId}/candidates`}
              className={({ isActive }) => `tab-btn-v2 ${isActive ? "active" : ""}`}
            >
              Candidates
            </NavLink>
            <NavLink
              to={`/jobs/${jobId}/details`}
              className={({ isActive }) => `tab-btn-v2 ${isActive ? "active" : ""}`}
            >
              Job Details
            </NavLink>
            <NavLink
              to={`/jobs/${jobId}/settings`}
              className={({ isActive }) => `tab-btn-v2 ${isActive ? "active" : ""}`}
            >
              Job Settings
            </NavLink>
            <NavLink
              to={`/jobs/${jobId}/automation`}
              className={({ isActive }) => `tab-btn-v2 ${isActive ? "active" : ""}`}
            >
              Automation
            </NavLink>
          </nav>
        </div>

        <div className="tab-content-v2">
          <Outlet />
        </div>
      </div>
    </AutomationContext.Provider>
  );
};

export default JobTabsV2;
